import { Injectable, Optional } from '@angular/core';
import { HttpClient, HttpEventType, HttpHeaders } from '@angular/common/http';


/** The shape your uploader function should return for the component */
export interface RmsUploadResult { url: string }


/** Optional config when using the HTTP uploader */
export interface RmsHttpUploadOptions {
  /** POST endpoint that accepts multipart/form-data */
  endpoint: string;
  /** Name of the form field that carries the file */
  fieldName?: string; // default: 'file'
  /** Any extra fields to send along with the file */
  extraFields?: Record<string, any>;
  /** Extra headers (e.g., auth) */
  headers?: Record<string, string>;
  /** If the API returns the URL under a different property, specify it */
  responseUrlPath?: string; // e.g. 'data.url'
}


@Injectable({ providedIn: 'root' })
export class RmsUploadService {
  constructor(@Optional() private http?: HttpClient) { }


  /**
  * PLACEHOLDER (safe default): Simulate an upload with a smooth progress bar.
  * Use this until your backend/Azure flow is wired up.
  */
  async simulateUpload(file: File, onProgress: (p: number) => void): Promise<RmsUploadResult> {
    let p = 0;
    await new Promise<void>((resolve) => {
      const id = setInterval(() => {
        p = Math.min(100, p + Math.max(1, Math.round(8 + Math.random() * 12)));
        onProgress(p >= 99 ? 99 : p); // keep UI at 99% until resolve
        if (p >= 100) { clearInterval(id); resolve(); }
      }, 80);
    });
    // Fake URL so your UI can treat it as uploaded
    return { url: URL.createObjectURL(file) };
  }
  /**
* Example uploader using Angular HttpClient to a classic multipart/form-data endpoint.
* Your Node API would accept the file and respond with a JSON payload containing a URL.
*/
  async uploadWithHttp(options: RmsHttpUploadOptions, file: File, onProgress: (p: number) => void): Promise<RmsUploadResult> {
    if (!this.http) throw new Error('HttpClient is not provided. Import HttpClientModule and inject HttpClient.');


    const form = new FormData();
    form.append(options.fieldName ?? 'file', file, file.name);
    if (options.extraFields) {
      for (const [k, v] of Object.entries(options.extraFields)) form.append(k, String(v));
    }


    const headers = new HttpHeaders(options.headers ?? {});


    return await new Promise<RmsUploadResult>((resolve, reject) => {
      this.http!.post(options.endpoint, form, { observe: 'events', reportProgress: true, headers })
        .subscribe({
          next: (evt) => {
            if (evt.type === HttpEventType.UploadProgress && evt.total) {
              const pct = Math.round(100 * (evt.loaded / evt.total));
              onProgress(Math.min(99, pct));
            } else if (evt.type === HttpEventType.Response) {
              try {
                const body: any = evt.body ?? {};
                const url = this.pickUrl(body, options.responseUrlPath) ?? body.url ?? body.Location ?? body.data?.url;
                if (!url) throw new Error('Upload succeeded but no URL returned.');
                resolve({ url });
              } catch (e) { reject(e); }
            }
          },
          error: (err) => reject(err)
        });
    });
  }
  /**
* Minimal Azure Blob SAS uploader (no SDK), using a precomputed SAS URL for the blob.
* Back-end should generate the SAS URL and hand it to the client for each upload.
*
* Example SAS URL shape:
* https://<account>.blob.core.windows.net/<container>/<blobName>?<SAS>
*/
  async uploadToAzureBlobSas(sasUrl: string, file: File, onProgress: (p: number) => void): Promise<RmsUploadResult> {
    const xhr = new XMLHttpRequest();
    return await new Promise<RmsUploadResult>((resolve, reject) => {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const pct = Math.round((e.loaded / e.total) * 100);
          onProgress(Math.min(99, pct));
        }
      };
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status >= 200 && xhr.status < 300) {
            // The final blob URL is the SAS URL without the query string
            const url = sasUrl.split('?')[0];
            resolve({ url });
          } else {
            reject(new Error(`Azure upload failed (${xhr.status}): ${xhr.responseText || xhr.statusText}`));
          }
        }
      };
      xhr.open('PUT', sasUrl, true);
      xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
      // Optional content-type helps preview in portals
      if (file.type) xhr.setRequestHeader('x-ms-blob-content-type', file.type);
      xhr.send(file);
    });
  }
  /**
* Convenience entry-point used by <rms-uploader> until your real backend is ready.
* Swap the implementation here when you finalize your upload flow.
*/
  async uploadToApi(file: File, onProgress: (p: number) => void): Promise<RmsUploadResult> {
    // 1) Placeholder simulated upload (keeps the UI happy)
    return this.simulateUpload(file, onProgress);


    // 2) Example: traditional HTTP multipart upload
    // return this.uploadWithHttp({ endpoint: '/api/files/upload', responseUrlPath: 'data.url' }, file, onProgress);


    // 3) Example: Azure Blob direct upload via SAS (backend must mint SAS first)
    // const sasUrl = await this.getSasUrlFromBackend(file.name, file.type);
    // return this.uploadToAzureBlobSas(sasUrl, file, onProgress);
  }


  /** Utility to dereference deep paths like 'data.url' */
  private pickUrl(obj: any, path?: string | undefined): string | undefined {
    if (!path) return undefined;
    return path.split('.').reduce<any>((acc, key) => (acc ? acc[key] : undefined), obj);
  }


  // Example placeholder: fetch SAS from your backend (implement server-side)
  // private async getSasUrlFromBackend(fileName: string, contentType: string): Promise<string> {
  // if (!this.http) throw new Error('HttpClient not provided.');
  // const res = await this.http.post<{ sasUrl: string }>('/api/blob/sas', { fileName, contentType }).toPromise();
  // if (!res?.sasUrl) throw new Error('No SAS URL returned');
  // return res.sasUrl;
  // }
}
